/**
 * ข้อเสนอหนึ่งรายการ พร้อมคำตัดสิน `approve` / `reject`
 *
 * กติกาสี่ข้อที่ทั้งไฟล์นี้ยืนอยู่บน — สามข้อแรกเหมือน `ProductTransitionPanel`
 * เพราะเป็นปัญหาเดียวกัน (คำสั่งที่กดจากหน้าจอที่อาจเปิดค้างไว้):
 *
 * 1. **`expected_status` คือสิ่งที่ถูกเรนเดอร์ ไม่ใช่สิ่งที่โค้ดคิดว่าควรเป็น**
 *    ค่าที่ส่งอ่านจาก `changeRequest.status` ก้อนเดียวกับที่ผู้ใช้กำลังมองตอนกด
 * 2. **`409` ไม่ลองใหม่ให้อัตโนมัติ** ดึงของจริงมาแสดง **หนึ่งครั้ง** แล้วให้คน
 *    ตัดสินใหม่ — และที่นี่ `409` ยังมีความหมายที่แรงกว่าของ transition ด้วย:
 *    `details.stale_fields` แปลว่ามีคนอื่นแก้ฟิลด์เหล่านั้นไปแล้ว **หลังจาก**
 *    ข้อเสนอนี้ถูกเขียน การกดซ้ำให้ผ่านคือการเขียนทับงานของคนนั้นเงียบ ๆ
 *    ซึ่งเป็นอุบัติเหตุที่ตาราง `product_change_request` มีไว้ป้องกันพอดี (D-042)
 * 3. **ความล้มเหลวห้ามขยับสิ่งที่แสดงอยู่** ทั้งสถานะของข้อเสนอและค่าของ record
 *    จะเปลี่ยนก็ต่อเมื่อเซิร์ฟเวอร์ตอบกลับมาเท่านั้น ไม่มีการเดาผลล่วงหน้า
 * 4. **`422` สองแบบต้องอ่านออกคนละเรื่อง** `APPROVER_MUST_DIFFER_FROM_CREATOR`
 *    ที่นี่หมายถึง "ผู้ยื่นคำขอนี้อนุมัติคำขอของตัวเองไม่ได้" ไม่ใช่ผู้สร้างสินค้า
 *    ส่วน `details.open_references` คือคำตอบว่ายังมีอะไรอ้างถึงสินค้านี้อยู่ ซึ่ง
 *    เป็นสิ่งที่ผู้ใช้ต้องไปจัดการต่อ ไม่ใช่ข้อความผิดพลาดให้ปิดทิ้ง
 *
 * ⚠️ การแสดง/ไม่แสดงปุ่มที่นี่เป็น **ความสะดวก ไม่ใช่ด่านความปลอดภัย**
 * เซิร์ฟเวอร์ตรวจทั้งสิทธิ์ สถานะ และ D-010 เองทุกคำขอเสมอ
 */

import { useEffect, useRef, useState } from 'react';

import { ProductFailureNotice } from './ProductFailureNotice';
import {
  changeRequestStatusLabel,
  changeRequestStatusTone,
  changeTypeLabel,
  orDash,
} from './productView';
import { StatusBadge } from '../StatusBadge';
import {
  decideProductChangeRequest,
  formatChangeValue,
  openReferencesFromFailure,
  staleFieldsFromFailure,
  type ProductFailure,
} from '../../lib/products/client';
import {
  PRODUCT_CHANGE_GENERAL_FIELDS,
  PRODUCT_CHANGE_PLANT_FIELDS,
  PRODUCT_CHANGE_REQUEST_DECISIONS,
  PRODUCT_CHANGE_REQUEST_OPEN_STATUS,
  type Product,
  type ProductChangeRequest,
  type ProductChangeRequestDecision,
} from '../../lib/products/types';

/** ผลของการดึงข้อมูลใหม่หลัง `409` — `null` คือยังไม่ได้ลอง (ความล้มเหลวแบบอื่น) */
export type RefreshOutcome = 'ok' | 'failed';

export interface ProductChangeRequestCardProps {
  readonly changeRequest: ProductChangeRequest;
  /** record ที่เซิร์ฟเวอร์ยืนยันล่าสุด — ใช้บอกว่า "ตอนนี้" ค่าจริงคืออะไร */
  readonly product: Product;
  readonly canApprove: boolean;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** ดึงทั้ง record และรายการข้อเสนอใหม่ **หนึ่งครั้ง** — ไม่ใช่การสั่งซ้ำ */
  readonly onRefresh: () => Promise<RefreshOutcome>;
}

type CardState =
  | { readonly status: 'idle' }
  | { readonly status: 'working'; readonly decision: ProductChangeRequestDecision }
  | {
      readonly status: 'done';
      readonly decision: ProductChangeRequestDecision;
      readonly resultStatus: string;
    }
  | {
      readonly status: 'failed';
      readonly decision: ProductChangeRequestDecision;
      readonly failure: ProductFailure;
      readonly refreshed: RefreshOutcome | null;
    };

const ACTION_LABELS: Readonly<Record<ProductChangeRequestDecision, string>> = {
  approve: 'อนุมัติคำขอ',
  reject: 'ไม่อนุมัติคำขอ',
};

const BUSY_LABELS: Readonly<Record<ProductChangeRequestDecision, string>> = {
  approve: 'กำลังอนุมัติ…',
  reject: 'กำลังบันทึกการไม่อนุมัติ…',
};

const DONE_LABELS: Readonly<Record<ProductChangeRequestDecision, string>> = {
  approve: 'อนุมัติคำขอแล้ว',
  reject: 'บันทึกการไม่อนุมัติคำขอแล้ว',
};

/**
 * ค่า "ตอนนี้" ของฟิลด์หนึ่ง อ่านจาก record ที่เพิ่งดึงมา ไม่ใช่จาก `baseline`
 *
 * นี่คือทั้งหมดของสิ่งที่ผู้อนุมัติต้องเห็นตอนเจอ `409`: baseline บอกว่าตอนเสนอ
 * ค่าเป็นอะไร ส่วนตรงนี้บอกว่าตอนนี้เป็นอะไร — ผลต่างของสองค่านั้นคืองานของคนที่
 * แก้ไปก่อน ซึ่งกำลังจะถูกเขียนทับถ้ากดอนุมัติต่อไปโดยไม่ดู
 */
function currentValueOf(product: Product, plantCode: string | null, field: string): string {
  let snapshot: Record<string, unknown>;

  if (plantCode === null) {
    snapshot = {};
    for (const known of PRODUCT_CHANGE_GENERAL_FIELDS) {
      snapshot[known] = product[known];
    }
  } else {
    const plant = product.plant_data.find((entry) => entry.plant_code === plantCode);
    if (plant === undefined) {
      // เกิดขึ้นได้จริงเมื่อ plant scope ของผู้เรียกกรองโรงงานนั้นออกไป (D-004) —
      // พูดตามตรงว่ามองไม่เห็น ดีกว่าแสดงขีดที่อ่านเหมือน "ไม่มีค่า"
      return 'ไม่ใช่โรงงานที่คุณมีสิทธิ์เห็น';
    }
    snapshot = {};
    for (const known of PRODUCT_CHANGE_PLANT_FIELDS) {
      snapshot[known] = plant[known];
    }
  }

  // ชุดฟิลด์ข้างบนอ้าง `PRODUCT_CHANGE_*_FIELDS` ชุดเดียวกับที่ฟอร์มใช้ ฟิลด์ที่
  // เซิร์ฟเวอร์เอ่ยถึงแต่หน้าจอยังไม่รู้จักจึงถูกบอกตรง ๆ ว่าไม่รู้จัก แทนที่จะ
  // แสดงเป็นค่าว่างซึ่งอ่านได้ว่า "ตอนนี้ไม่มีค่า"
  return field in snapshot
    ? formatChangeValue(snapshot[field])
    : 'หน้าจอนี้ยังไม่รู้จักฟิลด์นี้';
}

export function ProductChangeRequestCard({
  changeRequest,
  product,
  canApprove,
  apiBaseUrl,
  fetchImpl,
  onRefresh,
}: ProductChangeRequestCardProps) {
  const [state, setState] = useState<CardState>({ status: 'idle' });

  // ผลของ request ที่กลับมาหลัง component ถูกถอดออกแล้วต้องไม่ถูกเขียนลง state
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * คีย์ทั้งหมดที่ต้องแสดง — union ของ `proposed_changes` และ `baseline`
   *
   * ใช้ union ไม่ใช่คีย์ของฝั่งใดฝั่งหนึ่ง เพราะข้อเสนอที่แสดงไม่ครบคือข้อเสนอที่
   * ถูกอนุมัติโดยที่ผู้อนุมัติไม่เห็นว่าตกลงอะไรไปบ้าง
   */
  const changedFields = Array.from(
    new Set([
      ...Object.keys(changeRequest.proposed_changes),
      ...Object.keys(changeRequest.baseline),
    ]),
  );

  /**
   * เสนอคำตัดสินก็ต่อเมื่อ flag ที่เซิร์ฟเวอร์ส่งมามี `can_approve` จริง และ
   * ข้อเสนอยังเปิดอยู่ — ปุ่มบนข้อเสนอที่ถูกตัดสินไปแล้วจะล้มเหลวเสมอ
   */
  const offersDecision =
    canApprove && changeRequest.status === PRODUCT_CHANGE_REQUEST_OPEN_STATUS;

  const run = async (decision: ProductChangeRequestDecision) => {
    if (state.status === 'working') {
      return;
    }

    // ⚠️ อ่านตรงนี้ ตอนกด และจากก้อนที่กำลังถูกแสดงอยู่
    const expectedStatus = changeRequest.status;
    setState({ status: 'working', decision });

    const result = await decideProductChangeRequest(
      product.product_id,
      changeRequest.change_request_id,
      decision,
      expectedStatus,
      { apiBaseUrl, fetchImpl },
    );
    if (!mounted.current) {
      return;
    }

    if (result.status === 'ready') {
      setState({ status: 'done', decision, resultStatus: result.changeRequest.status });
      // approve ที่สำเร็จเปลี่ยน record จริงด้วย จึงต้องดึงทั้งสองอย่างมาใหม่
      await onRefresh();
      return;
    }

    // สิ่งที่แสดงอยู่ไม่ถูกแตะ — ความล้มเหลวไม่ใช่การเปลี่ยนสถานะ
    setState({ status: 'failed', decision, failure: result, refreshed: null });
    if (result.status !== 'conflict') {
      return;
    }

    // `409` เท่านั้นที่ตามด้วยการอ่านใหม่ และเป็นการ **อ่าน** ไม่ใช่การสั่งซ้ำ
    const refreshed = await onRefresh();
    if (!mounted.current) {
      return;
    }
    setState({ status: 'failed', decision, failure: result, refreshed });
  };

  const failure = state.status === 'failed' ? state.failure : null;
  const staleFields = failure === null ? [] : staleFieldsFromFailure(failure);
  const openReferences = failure === null ? [] : openReferencesFromFailure(failure);

  return (
    <article
      className="cap-card cap-card--table"
      data-testid={`product-cr-${changeRequest.change_request_id}`}
      aria-label={`คำขอเปลี่ยนแปลง ${changeRequest.change_request_id}`}
    >
      <div className="cap-card__head">
        <h3 className="cap-card__title">
          {changeTypeLabel(changeRequest.change_type)}{' '}
          <StatusBadge tone={changeRequestStatusTone(changeRequest.status)}>
            {changeRequestStatusLabel(changeRequest.status)}
          </StatusBadge>
        </h3>
        <span className="cap-card__hint">
          ยื่นเมื่อ {changeRequest.created_at} โดย{' '}
          <span data-testid={`product-cr-creator-${changeRequest.change_request_id}`}>
            {orDash(changeRequest.created_by_employee_id)}
          </span>
        </span>
      </div>

      <div className="cap-meta cap-meta--flush">
        <div className="cap-meta__item">
          <div className="cap-meta__label">ขอบเขต</div>
          <div
            className="cap-meta__value"
            data-testid={`product-cr-scope-${changeRequest.change_request_id}`}
          >
            {changeRequest.plant_code === null
              ? 'ระดับสินค้า (ทุกโรงงาน)'
              : `โรงงาน ${changeRequest.plant_code}`}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">ผู้ตัดสิน</div>
          <div className="cap-meta__value">
            {orDash(changeRequest.approved_by_employee_id)}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">เวลาที่ตัดสิน</div>
          <div className="cap-meta__value">{orDash(changeRequest.approved_at)}</div>
        </div>
      </div>

      {changedFields.length === 0 ? (
        <p
          className="cap-card__foot"
          data-testid={`product-cr-no-fields-${changeRequest.change_request_id}`}
        >
          คำขอนี้ไม่ได้เสนอแก้ฟิลด์ใดเลย — เป็นรูปร่างที่ถูกต้องของคำขอปิดใช้งาน (D-042)
        </p>
      ) : (
        <div className="cap-table-wrap">
          <table className="cap-table">
            <caption className="cap-visually-hidden">
              ฟิลด์ที่ข้อเสนอนี้ขอแก้ เทียบค่าเดิม ณ ตอนเสนอ กับค่าที่เสนอใหม่
            </caption>
            <thead>
              <tr>
                <th scope="col">ฟิลด์</th>
                <th scope="col">ค่าเดิม ณ ตอนเสนอ (baseline)</th>
                <th scope="col">ค่าที่เสนอใหม่</th>
                <th scope="col">ค่าจริงตอนนี้</th>
              </tr>
            </thead>
            <tbody>
              {changedFields.map((field) => (
                <tr key={field} data-testid={`product-cr-field-${field}`}>
                  <th scope="row" className="cap-table__row-head">
                    {field}
                  </th>
                  <td className="cap-table__muted" data-testid={`product-cr-baseline-${field}`}>
                    {formatChangeValue(changeRequest.baseline[field])}
                  </td>
                  <td className="cap-table__code" data-testid={`product-cr-proposed-${field}`}>
                    {formatChangeValue(changeRequest.proposed_changes[field])}
                  </td>
                  <td className="cap-table__code" data-testid={`product-cr-current-value-${field}`}>
                    {currentValueOf(product, changeRequest.plant_code, field)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="cap-card__foot">
        {offersDecision ? (
          <div className="cap-form__actions">
            {PRODUCT_CHANGE_REQUEST_DECISIONS.map((decision) => (
              <button
                key={decision}
                type="button"
                className={`cap-button ${
                  decision === 'reject' ? 'cap-button--ghost' : 'cap-button--primary'
                }`}
                data-testid={`product-cr-${decision}-${changeRequest.change_request_id}`}
                disabled={state.status === 'working'}
                onClick={() => {
                  void run(decision);
                }}
              >
                {state.status === 'working' && state.decision === decision
                  ? BUSY_LABELS[decision]
                  : ACTION_LABELS[decision]}
              </button>
            ))}
          </div>
        ) : (
          <p data-testid={`product-cr-no-decision-${changeRequest.change_request_id}`}>
            {canApprove
              ? 'คำขอนี้ถูกตัดสินไปแล้ว จึงไม่มีคำสั่งให้สั่งจากหน้าจอนี้'
              : 'สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี can_approve ของโมดูล PRODUCT_MASTER จึงไม่แสดงคำสั่งอนุมัติ'}
          </p>
        )}
      </div>

      {state.status === 'done' ? (
        <div
          className="cap-banner"
          role="status"
          data-testid={`product-cr-done-${changeRequest.change_request_id}`}
        >
          <strong>{DONE_LABELS[state.decision]}</strong>
          <span>
            สถานะล่าสุดที่เซิร์ฟเวอร์ยืนยันคือ {changeRequestStatusLabel(state.resultStatus)} (
            {state.resultStatus})
          </span>
        </div>
      ) : null}

      {failure === null ? null : (
        <>
          <ProductFailureNotice failure={failure} context="change-request" />

          {staleFields.length === 0 ? null : (
            <div
              className="cap-notice"
              role="status"
              data-testid={`product-cr-stale-${changeRequest.change_request_id}`}
            >
              <StatusBadge tone="warning">มีคนแก้ฟิลด์เหล่านี้ไปก่อนแล้ว</StatusBadge>
              <p className="cap-notice__text">
                เซิร์ฟเวอร์ตอบ 409 พร้อม <code className="cap-table__code">details.stale_fields</code>{' '}
                — ค่าปัจจุบันของฟิลด์ด้านล่างไม่ตรงกับ baseline ของข้อเสนอนี้อีกแล้ว
                แปลว่ามีคนอื่นแก้ไปหลังจากข้อเสนอนี้ถูกเขียน ระบบจึงไม่ apply ทับให้
                และจะไม่ส่งคำสั่งเดิมซ้ำให้อัตโนมัติ
              </p>
              <div className="cap-table-wrap">
                <table className="cap-table">
                  <caption className="cap-visually-hidden">
                    ฟิลด์ที่ค่าปัจจุบันไม่ตรงกับ baseline ของข้อเสนอนี้
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">ฟิลด์</th>
                      <th scope="col">baseline ของข้อเสนอ</th>
                      <th scope="col">ค่าจริงตอนนี้</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staleFields.map((field) => (
                      <tr key={field} data-testid={`product-cr-stale-field-${field}`}>
                        <th scope="row" className="cap-table__row-head">
                          {field}
                        </th>
                        <td className="cap-table__muted">
                          {formatChangeValue(changeRequest.baseline[field])}
                        </td>
                        <td className="cap-table__code" data-testid={`product-cr-stale-now-${field}`}>
                          {currentValueOf(product, changeRequest.plant_code, field)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {openReferences.length === 0 ? null : (
            <div
              className="cap-notice"
              role="status"
              data-testid={`product-cr-open-references-${changeRequest.change_request_id}`}
            >
              <StatusBadge tone="warning">ยังมีงานอ้างถึงสินค้านี้อยู่</StatusBadge>
              <p className="cap-notice__text">
                เซิร์ฟเวอร์ตอบ 422 พร้อม{' '}
                <code className="cap-table__code">details.open_references</code> — ปิดใช้งานสินค้านี้
                ไม่ได้จนกว่าจะจัดการรายการด้านล่างก่อน
              </p>
              {/* ไม่ใส่ class ของ design system เพราะ `desktop.css` ยังไม่มีคลาสของ
                  รายการแบบ bullet และ stylesheet อยู่นอก write scope ของงานนี้ */}
              <ul>
                {openReferences.map((reference) => (
                  <li key={reference}>{reference}</li>
                ))}
              </ul>
            </div>
          )}

          {state.status === 'failed' && state.refreshed !== null ? (
            <p
              className="cap-banner__note"
              data-testid={`product-cr-refresh-${changeRequest.change_request_id}`}
            >
              {state.refreshed === 'ok'
                ? 'ดึงข้อมูลล่าสุดของรายการและคำขอมาแสดงแทนแล้ว และไม่ได้ส่งคำสั่งเดิมซ้ำ — ตรวจค่าจริงก่อนตัดสินใจใหม่'
                : 'ดึงข้อมูลล่าสุดไม่สำเร็จ สิ่งที่เห็นบนหน้าจออาจไม่ใช่ค่าจริงแล้ว — เปิดหน้านี้ใหม่ก่อนตัดสินใจ'}
            </p>
          ) : null}
        </>
      )}
    </article>
  );
}
